import React, { useState, useEffect } from 'react';
import LoadingContext from './LoadingContext';

/**
 * A context provider component that manages the global loading state.
 * It keeps track of the number of active loading operations and exposes methods based on loading keys
 * to show and hide the loading indicator.
 *
 * @param children The child components to be rendered within the loading context.
 * @returns The LoadingContext.Provider with the current loading state and methods to show and hide the loading indicator.
 */
const LoadingProvider: React.FC<{ children?: React.ReactNode }>= ({ children }) => {
  // Keep track of the source of active loading operations
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({});
  const [initialLoadTime, setInitialLoadTime] = useState<number | null>(null);
  const [showConsoleLogs, setShowConsoleLogs] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  /**
   * A flag to show the total loading time.
   * This constant is only set inside the function.
   */
  const ShowTotalLoadTime: boolean = false;

  // Expose methods to show and hide the loading indicator
  const showLoading = (source: string = '', shouldLog?: boolean) => {
    if (shouldLog && source) {
      console.log(`[LoadingProvider] showLoading called by: ${source}`);
    }

    if (Object.values(loadingStates).every((state) => !state)) {
      setInitialLoadTime(performance.now());
    }

    setLoadingStates((prev) => ({ ...prev, [source]: true }));
    setShowConsoleLogs(!!shouldLog);
  };

  // Expose methods to show and hide the loading indicator
  const hideLoading = (source: string = '') => {
    if (showConsoleLogs && source) {
      console.log(`[LoadingProvider] hideLoading called by: ${source}`);
    }

    setLoadingStates((prev) => ({ ...prev, [source]: false }));
    setShowConsoleLogs(false);
  };

  useEffect(() => {
    setIsLoading(Object.values(loadingStates).some((state) => state));
  }, [loadingStates]);

  // Log the total load time when all loading operations have finished
  useEffect(() => {
    if (
      !isLoading &&
      initialLoadTime !== null &&
      (showConsoleLogs || ShowTotalLoadTime)
    ) {
      const totalTime = performance.now() - initialLoadTime;
      console.log(
        `[LoadingProvider] Total load time: ${totalTime.toFixed(2)} ms`
      );
    }
  }, [isLoading, initialLoadTime, showConsoleLogs]);

  return (
    <LoadingContext.Provider
      value={{ isLoading, showLoading, hideLoading, showConsoleLogs }}
    >
      {children}
    </LoadingContext.Provider>
  );
};

export default LoadingProvider;
